<template>
  <b-card
    v-if="verificarPermissao('ROLE_AJUSTE_DE_CONTA_CONSULTAR')"
    title="Novo Ajuste de Conta"
  >
    <b-alert
      :variant="alertConfig.variant"
      show
    >
      <div class="alert-body">
        <feather-icon
          :icon="alertConfig.icon"
          class="mr-50"
        />
        <span v-html="alertConfig.message" />
      </div>
    </b-alert>

    <b-form @submit.prevent="save">
      <b-row>
        <!-- Tipo de Ajuste -->
        <b-col md="6">
          <b-form-group label="Tipo de Ajuste *">
            <b-form-radio-group
              v-model="form.tipoContabil"
              name="tipo-ajuste"
              class="w-100 radio-custom-types"
              buttons
              required
            >
              <b-form-radio
                value="RECEITA"
                button-variant="outline-success"
              >
                Crédito (Adicionar)
              </b-form-radio>
              <b-form-radio
                value="DESPESA"
                button-variant="outline-danger"
              >
                Débito (Subtrair)
              </b-form-radio>
            </b-form-radio-group>
          </b-form-group>
        </b-col>

        <!-- Conta -->
        <b-col md="6">
          <b-form-group label="Conta *">
            <v-select
              v-model="form.conta"
              :options="contasOptions"
              label="descricao"
              placeholder="Selecione a conta"
              :reduce="conta => conta"
              required
            >
              <template #option="{ descricao, banco, agencia, conta }">
                <div class="d-flex flex-column">
                  <span><strong>{{ descricao }}</strong></span>
                  <small
                    v-if="banco"
                    class="text-muted"
                  >{{ banco.nome }} - Ag: {{ agencia }} CC: {{ conta }}</small>
                </div>
              </template>
            </v-select>
          </b-form-group>
        </b-col>

        <!-- Valor -->
        <b-col md="4">
          <b-form-group label="Valor (R$) *">
            <money
              v-model="form.valor"
              v-bind="money"
              class="form-control text-right"
              required
            />
          </b-form-group>
        </b-col>

        <!-- Data -->
        <b-col md="4">
          <b-form-group label="Data do Ajuste *">
            <b-form-input
              v-model="form.data"
              type="date"
              required
            />
          </b-form-group>
        </b-col>

        <!-- Observações / Motivo -->
        <b-col
          md="12"
          class="mt-1"
        >
          <b-form-group label="Motivo do Ajuste *">
            <b-form-textarea
              v-model="form.obs"
              rows="4"
              placeholder="Descreva detalhadamente o motivo que levou ao ajuste do saldo..."
              required
            />
          </b-form-group>
        </b-col>
      </b-row>

      <hr>
      <ButtonsFormVue
        :disable-form="false"
        :permission-insert="verificarPermissao('ROLE_AJUSTE_DE_CONTA_INSERIR')"
        save-button-text="Registrar Ajuste"
        @save="save"
        @cancel="cancel"
      />
    </b-form>
  </b-card>
</template>

<script>
import {
  BRow, BCol, BCard, BForm, BFormGroup, BFormTextarea, BButton, BAlert, BFormRadioGroup, BFormRadio, BFormInput,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { Money } from 'v-money'
import { mapState } from 'vuex'
import ButtonsFormVue from '@/layouts/components/ButtonsForm.vue'
import mixinsGlobal from '@/mixins'

export default {
  name: 'AjusteContaForm',
  components: {
    BRow,
    BCol,
    BCard,
    BForm,
    BFormGroup,
    BFormTextarea,
    BButton,
    BAlert,
    BFormRadioGroup,
    BFormRadio,
    BFormInput,
    vSelect,
    Money,
    ButtonsFormVue,
  },
  mixins: [mixinsGlobal],
  data() {
    return {
      form: {
        id: '',
        obs: '',
        conta: null,
        data: new Date().toISOString().substring(0, 10), // Hoje
        valor: 0.0,
        tipoContabil: 'RECEITA',
      },
      money: {
        decimal: ',',
        thousands: '.',
        prefix: '',
        suffix: '',
        precision: 2,
        masked: false,
      },
    }
  },
  computed: {
    alertConfig() {
      if (this.form.tipoContabil === 'RECEITA') {
        return {
          variant: 'success',
          icon: 'TrendingUpIcon',
          message: '<strong>Crédito:</strong> Você está realizando uma <strong>ENTRADA</strong> manual. O valor será <strong>ADICIONADO</strong> ao saldo da conta.',
        }
      }
      return {
        variant: 'danger',
        icon: 'TrendingDownIcon',
        message: '<strong>Débito:</strong> Você está realizando uma <strong>SAÍDA</strong> manual. O valor será <strong>SUBTRAÍDO</strong> do saldo da conta.',
      }
    },
    ...mapState('combosModule', {
      contasOptions: state => state.comboContas || [],
    }),
  },
  async mounted() {
    this.$store.dispatch('ajusteContaFinancialModule/reset')

    // Load combo contas if not loaded
    if (this.$store._actions['combosModule/loadComboContas']) {
      await this.$store.dispatch('combosModule/loadComboContas')
    }

    // Auto select conta from URL
    if (this.$route.query.contaId) {
      setTimeout(() => {
        const preSelected = this.contasOptions.find(c => String(c.id) === String(this.$route.query.contaId))
        if (preSelected) {
          this.form.conta = preSelected
        }
      }, 500)
    }
  },
  methods: {
    async save() {
      // Validações Manuais
      if (!this.form.conta) {
        this.MensagemError('Validação', 'Selecione a conta para o ajuste.')
        return
      }
      if (this.form.valor <= 0) {
        this.MensagemError('Validação', 'O valor do ajuste deve ser maior que zero.')
        return
      }
      if (!this.form.obs || this.form.obs.trim() === '') {
        this.MensagemError('Validação', 'O motivo do ajuste é obrigatório.')
        return
      }

      const payload = { ...this.form }

      try {
        const response = await this.$store.dispatch('ajusteContaFinancialModule/insert', payload)

        if (response && response.data && response.data.id) {
          this.showMessageSuccess('Sucesso', 'Ajuste de Conta registrado e saldo atualizado com sucesso!')
          this.$router.push({ name: 'contas' })
        } else if (response.response && response.response.data && response.response.data.errors) {
          this.MensagemError('Erro', response.response.data.errors[0].message)
        } else {
          this.MensagemError('Erro', 'Falha ao registrar o ajuste de conta.')
        }
      } catch (error) {
        this.MensagemError('Erro', 'Ocorreu um erro na requisição.')
      }
    },
    cancel() {
      this.$router.push({ name: 'contas' })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/base/bootstrap-extended/_variables.scss';

$success: $green;
$danger: $red;

.radio-custom-types {
  display: flex;
  gap: 10px;

  ::v-deep {
    .btn-group {
      display: flex;
      width: 100%;
      gap: 10px;

      // Reset Bootstrap's btn-group behavior that collapses borders/radius
      & > .btn {
        margin-left: 0 !important;
        border-radius: 0.358rem !important;
        border: 1px solid !important; // Re-enables missing borders
      }
    }

    // Estilo para os botões de rádio
    label.btn {
      flex: 1;
      font-weight: 500;
      padding: 0.786rem 1.5rem;
      transition: all 0.2s ease-in-out;

      // Override standard outline behavior for better UX
      &.active {
        box-shadow: 0 4px 8px rgba(0,0,0,0.1) !important;
      }
    }
  }
}
</style>
